import { useState, useCallback, useRef } from 'react'
import dagre from 'dagre'

const NODE_WIDTH = 280
const NODE_HEIGHT = 120
const ADD_BTN_WIDTH = 160
const ADD_BTN_HEIGHT = 40

let idCounter = 0
const nextId = (prefix = 'node') => `${prefix}_${++idCounter}`

function layoutNodes(nodes, edges) {
    const g = new dagre.graphlib.Graph()
    g.setGraph({ rankdir: 'TB', ranksep: 80, nodesep: 60 })
    g.setDefaultEdgeLabel(() => ({}))

    nodes.forEach((node) => {
        const w = node.type === 'addButton' ? ADD_BTN_WIDTH : NODE_WIDTH
        const h = node.type === 'addButton' ? ADD_BTN_HEIGHT : NODE_HEIGHT
        g.setNode(node.id, { width: w, height: h })
    })
    edges.forEach((edge) => {
        g.setEdge(edge.source, edge.target)
    })

    dagre.layout(g)

    return nodes.map((node) => {
        const pos = g.node(node.id)
        const w = node.type === 'addButton' ? ADD_BTN_WIDTH : NODE_WIDTH
        const h = node.type === 'addButton' ? ADD_BTN_HEIGHT : NODE_HEIGHT
        return {
            ...node,
            position: { x: pos.x - w / 2, y: pos.y - h / 2 },
        }
    })
}

const INITIAL_START_NODE = {
    id: 'start',
    type: 'start',
    data: {
        name: '',
        availableModel: null,
        triggerType: '',
        columnsUpdated: [],
        isActive: true,
    },
    position: { x: 0, y: 0 },
}

function buildInitialState() {
    const addCondBtn = {
        id: 'add-condition-group',
        type: 'addButton',
        data: { label: 'Add Condition Group', addType: 'conditionGroup' },
        position: { x: 0, y: 0 },
    }
    const addActionBtn = {
        id: 'add-action',
        type: 'addButton',
        data: { label: 'Add Action', addType: 'action' },
        position: { x: 0, y: 0 },
    }

    const nodes = [INITIAL_START_NODE, addCondBtn, addActionBtn]
    const edges = [
        { id: 'e-start-addcond', source: 'start', target: 'add-condition-group', type: 'smoothstep', animated: true },
        { id: 'e-addcond-addaction', source: 'add-condition-group', target: 'add-action', type: 'smoothstep', animated: true },
    ]

    return { nodes: layoutNodes(nodes, edges), edges }
}

function buildStateFromAutomation(automation) {
    idCounter = 0
    const nodes = []
    const edges = []

    const startNode = {
        id: 'start',
        type: 'start',
        data: {
            name: automation.name,
            isActive: automation.isActive,
            triggerType: automation.trigger?.triggerType || '',
            columnsUpdated: automation.trigger?.columnsUpdated || [],
            availableModel: null,
            triggerId: automation.trigger?.id,
            contentTypeId: automation.trigger?.contentType?.id,
            contentTypeModel: automation.trigger?.contentType?.model,
        },
        position: { x: 0, y: 0 },
    }
    nodes.push(startNode)

    let lastNodeId = 'start'

    const conditionGroups = automation.conditiongroupSet?.edges || []
    conditionGroups.forEach(({ node: cg }) => {
        const cgId = `cg_${cg.id}`
        const conditions = (cg.conditionSet?.edges || []).map(({ node: c }) => ({
            id: c.id,
            fieldName: c.fieldName,
            conditionType: c.conditionType,
            conditionValue: c.conditionValue,
            version: c.version || 'CURRENT',
        }))
        nodes.push({
            id: cgId,
            type: 'conditionGroup',
            data: {
                dbId: cg.id,
                logicalOperator: cg.logicalOperator,
                conditions,
            },
            position: { x: 0, y: 0 },
        })
        edges.push({
            id: `e-${lastNodeId}-${cgId}`,
            source: lastNodeId,
            target: cgId,
            type: 'smoothstep',
        })
        lastNodeId = cgId
    })

    const addCondBtn = {
        id: 'add-condition-group',
        type: 'addButton',
        data: { label: 'Add Condition Group', addType: 'conditionGroup' },
        position: { x: 0, y: 0 },
    }
    nodes.push(addCondBtn)
    edges.push({
        id: `e-${lastNodeId}-addcond`,
        source: lastNodeId,
        target: 'add-condition-group',
        type: 'smoothstep',
        animated: true,
    })

    const actionEdgeSource = conditionGroups.length > 0
        ? `cg_${conditionGroups[conditionGroups.length - 1].node.id}`
        : 'start'

    const actions = automation.actionSet?.edges || []
    actions.forEach(({ node: act }, idx) => {
        const actId = `action_${act.id}`
        let parsedParams = {}
        try {
            parsedParams = typeof act.actionParams === 'string'
                ? JSON.parse(act.actionParams.replace(/'/g, '"'))
                : act.actionParams || {}
        } catch { /* keep empty */ }

        nodes.push({
            id: actId,
            type: 'action',
            data: {
                dbId: act.id,
                actionType: act.actionType,
                actionParams: parsedParams,
            },
            position: { x: 0, y: 0 },
        })
        edges.push({
            id: `e-${actionEdgeSource}-${actId}`,
            source: actionEdgeSource,
            target: actId,
            type: 'smoothstep',
        })
    })

    const addActionBtn = {
        id: 'add-action',
        type: 'addButton',
        data: { label: 'Add Action', addType: 'action' },
        position: { x: 0, y: 0 },
    }
    nodes.push(addActionBtn)
    edges.push({
        id: `e-${actionEdgeSource}-addaction`,
        source: actionEdgeSource,
        target: 'add-action',
        type: 'smoothstep',
        animated: true,
    })

    return { nodes: layoutNodes(nodes, edges), edges }
}

export default function useWorkflowState(automationData) {
    const initialized = useRef(false)
    const [state, setState] = useState(() => {
        if (automationData) {
            initialized.current = true
            return buildStateFromAutomation(automationData)
        }
        return buildInitialState()
    })

    const loadFromAutomation = useCallback((automation) => {
        if (!automation) return
        const s = buildStateFromAutomation(automation)
        setState(s)
        initialized.current = true
    }, [])

    const reLayout = useCallback(() => {
        setState((prev) => ({
            ...prev,
            nodes: layoutNodes(prev.nodes, prev.edges),
        }))
    }, [])

    const updateNodeData = useCallback((nodeId, newData) => {
        setState((prev) => ({
            ...prev,
            nodes: prev.nodes.map((n) =>
                n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n
            ),
        }))
    }, [])

    const addConditionGroup = useCallback(() => {
        setState((prev) => {
            const cgId = nextId('cg_new')
            const newNode = {
                id: cgId,
                type: 'conditionGroup',
                data: {
                    logicalOperator: 'AND',
                    conditions: [],
                },
                position: { x: 0, y: 0 },
            }

            const incomingEdge = prev.edges.find((e) => e.target === 'add-condition-group')
            const parentId = incomingEdge ? incomingEdge.source : 'start'

            const newEdges = prev.edges.filter((e) => e.target !== 'add-condition-group')
            newEdges.push({
                id: `e-${parentId}-${cgId}`,
                source: parentId,
                target: cgId,
                type: 'smoothstep',
            })
            newEdges.push({
                id: `e-${cgId}-addcond`,
                source: cgId,
                target: 'add-condition-group',
                type: 'smoothstep',
                animated: true,
            })

            const actionEdges = prev.edges.filter(
                (e) => prev.nodes.find((n) => n.id === e.target && n.type === 'action')
            )
            const updatedActionEdges = actionEdges.map((e) => ({
                ...e,
                source: cgId,
            }))
            const addActionEdge = prev.edges.find((e) => e.target === 'add-action')
            const nonActionEdges = newEdges.filter(
                (e) => !actionEdges.find((ae) => ae.id === e.id) && e.target !== 'add-action'
            )
            const finalEdges = [
                ...nonActionEdges,
                ...updatedActionEdges,
                addActionEdge ? { ...addActionEdge, source: cgId } : null,
            ].filter(Boolean)

            const nodes = [...prev.nodes, newNode]
            return { nodes: layoutNodes(nodes, finalEdges), edges: finalEdges }
        })
    }, [])

    const removeConditionGroup = useCallback((nodeId) => {
        setState((prev) => {
            const incomingEdge = prev.edges.find((e) => e.target === nodeId)
            const outgoingEdges = prev.edges.filter((e) => e.source === nodeId)
            const parentId = incomingEdge ? incomingEdge.source : 'start'

            let newEdges = prev.edges.filter((e) => e.source !== nodeId && e.target !== nodeId)
            outgoingEdges.forEach((oe) => {
                newEdges.push({
                    id: `e-${parentId}-${oe.target}`,
                    source: parentId,
                    target: oe.target,
                    type: 'smoothstep',
                    animated: oe.animated,
                })
            })

            const nodes = prev.nodes.filter((n) => n.id !== nodeId)
            return { nodes: layoutNodes(nodes, newEdges), edges: newEdges }
        })
    }, [])

    const addAction = useCallback(() => {
        setState((prev) => {
            const actId = nextId('action_new')
            const newNode = {
                id: actId,
                type: 'action',
                data: {
                    actionType: '',
                    actionParams: {},
                },
                position: { x: 0, y: 0 },
            }

            const addActionEdge = prev.edges.find((e) => e.target === 'add-action')
            const sourceId = addActionEdge ? addActionEdge.source : 'start'

            const newEdges = [...prev.edges]
            newEdges.push({
                id: `e-${sourceId}-${actId}`,
                source: sourceId,
                target: actId,
                type: 'smoothstep',
            })

            const nodes = [...prev.nodes, newNode]
            return { nodes: layoutNodes(nodes, newEdges), edges: newEdges }
        })
    }, [])

    const removeAction = useCallback((nodeId) => {
        setState((prev) => {
            const newEdges = prev.edges.filter((e) => e.source !== nodeId && e.target !== nodeId)
            const nodes = prev.nodes.filter((n) => n.id !== nodeId)
            return { nodes: layoutNodes(nodes, newEdges), edges: newEdges }
        })
    }, [])

    const serializeForCreate = useCallback(() => {
        const startNode = state.nodes.find((n) => n.id === 'start')
        if (!startNode) return null
        const { name, isActive, triggerType, columnsUpdated, availableModel } = startNode.data

        const conditionGroupNodes = state.nodes.filter((n) => n.type === 'conditionGroup')

        let conditionGroup = { logicalOperator: 'AND', conditions: [], childGroups: [] }
        if (conditionGroupNodes.length === 1) {
            const cg = conditionGroupNodes[0].data
            conditionGroup = {
                logicalOperator: cg.logicalOperator,
                conditions: (cg.conditions || [])
                    .filter((c) => c.fieldName && c.conditionType)
                    .map((c) => ({
                        version: c.version || 'CURRENT',
                        fieldName: c.fieldName,
                        conditionType: c.conditionType,
                        conditionValue: c.conditionValue || '',
                    })),
                childGroups: [],
            }
        } else if (conditionGroupNodes.length > 1) {
            conditionGroup = {
                logicalOperator: 'AND',
                conditions: [],
                childGroups: conditionGroupNodes.map((cgNode) => ({
                    logicalOperator: cgNode.data.logicalOperator,
                    conditions: (cgNode.data.conditions || [])
                        .filter((c) => c.fieldName && c.conditionType)
                        .map((c) => ({
                            version: c.version || 'CURRENT',
                            fieldName: c.fieldName,
                            conditionType: c.conditionType,
                            conditionValue: c.conditionValue || '',
                        })),
                    childGroups: [],
                })),
            }
        }

        const actionNodes = state.nodes.filter((n) => n.type === 'action')
        const firstAction = actionNodes[0]
        if (!firstAction || !firstAction.data.actionType) return null

        return {
            name,
            isActive: isActive !== false,
            triggerType,
            availableModelId: availableModel?.id,
            columnsUpdated: columnsUpdated || [],
            conditionGroup,
            actionType: firstAction.data.actionType,
            actionParams: JSON.stringify(firstAction.data.actionParams || {}),
        }
    }, [state])

    return {
        nodes: state.nodes,
        edges: state.edges,
        updateNodeData,
        addConditionGroup,
        removeConditionGroup,
        addAction,
        removeAction,
        serializeForCreate,
        loadFromAutomation,
        reLayout,
        initialized: initialized.current,
    }
}
